import { useState } from "react";
import { Link } from "react-router-dom";
import { NavBar } from "@/components/NavBar";
import { SEO } from "@/components/SEO";
import { ArrowLeft, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    website: ""  // Honeypot field - hidden from users, bots fill it
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success("Message sent successfully!");
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please try again or email us directly at support@gottasecond.com");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Get in touch with the GottaSecond team. We're here to help studios and photographers with questions about hiring second shooters."
        path="/contact"
      />
      <NavBar>
        <Link to="/pricing" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Pricing</Link>
        <Link to="/login" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Login</Link>
        <Link to="/register" className="text-sm font-medium text-[#e8594a] hover:text-[#ff6b5a] transition-colors uppercase tracking-wider font-bold">Get Started</Link>
      </NavBar>

      <div className="max-w-xl mx-auto px-6 py-24">
        <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-white transition-colors mb-8 text-sm" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <h1 className="text-4xl font-bold mb-2 tracking-tight">Contact Us</h1>
        <p className="text-white/40 mb-8">Have a question or feedback? We'd love to hear from you.</p>

        {isSubmitted ? (
          <div className="text-center py-12 bg-white/5 rounded-lg border border-white/10">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Message Sent!</h2>
            <p className="text-white/60 mb-6">Thank you for contacting us. We'll get back to you as soon as possible.</p>
            <Link to="/">
              <Button variant="outline" className="border-white/20 hover:bg-white/10">
                Return Home
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Name *</label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="contact-name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Email *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="contact-email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Subject</label>
              <Input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="What's this about?"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="contact-subject"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Message *</label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="How can we help you?"
                rows={6}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
                data-testid="contact-message"
                required
              />
            </div>

            {/* Honeypot field - hidden from users, only bots will fill this */}
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-white text-black hover:bg-white/90 font-semibold"
              data-testid="contact-submit"
            >
              {isSubmitting ? (
                "Sending..."
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>

            <p className="text-white/40 text-sm text-center">
              Or email us directly at{" "}
              <a href="mailto:support@gottasecond.com" className="text-[#e8594a] hover:underline">
                support@gottasecond.com
              </a>
            </p>
          </form>
        )}
      </div>

      {/* Footer - 3 Column Layout */}
      <footer className="py-12 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            {/* Left - Logo & Powered by */}
            <div className="flex flex-col items-center md:items-start gap-2">
              <img src="https://customer-assets.emergentagent.com/job_20a21bfd-b8fc-46d5-80c1-3ef1449007db/artifacts/gnn6v1fg_Gotta%20Second-white.png" alt="GottaSecond" className="h-8 w-auto" />
              <span className="text-sm text-white/40">Powered by Studio Lifesaver</span>
            </div>
            
            {/* Middle - Links */}
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/40">
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/accessibility" className="hover:text-white transition-colors">Accessibility</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link>
              <Link to="/privacy#do-not-sell" className="hover:text-white transition-colors">Do Not Sell My Info</Link>
            </div>
            
            {/* Right - Copyright */}
            <div className="flex justify-center md:justify-end">
              <span className="text-sm text-white/40">© 2026 Studio Lifesaver LLC</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
